import { array, boolean, mixed, object, string } from 'yup';
import { trackingLinkSchema } from '@/schema/tracking-link-schema';
const modeSchema = mixed().oneOf(['classic', 'auto_store', 'feedback', 'showroom']).optional();
const isValidUrl = (value) => {
    if (!value) {
        return true;
    }
    try {
        new URL(value);
        return true;
    }
    catch {
        return false;
    }
};
const optionalUrl = () => string().optional().test('valid-url', 'Must be a valid URL', isValidUrl);
const matchesHost = (host, _message) => (value) => {
    if (!value) {
        return true;
    }
    if (!isValidUrl(value)) {
        // Defer to `valid-url` for the error message.
        return true;
    }
    try {
        const parsed = new URL(value);
        return host.test(parsed.hostname);
    }
    catch {
        return false;
    }
};
export const appStoreUrlSchema = string()
    .optional()
    .test('valid-url', 'Must be a valid URL', isValidUrl)
    .test('apps-apple-domain', 'App Store URL must be on apps.apple.com', matchesHost(/(^|\.)apps\.apple\.com$/i, 'App Store URL must be on apps.apple.com'));
export const googlePlayUrlSchema = string()
    .optional()
    .test('valid-url', 'Must be a valid URL', isValidUrl)
    .test('play-google-domain', 'Google Play URL must be on play.google.com', matchesHost(/(^|\.)play\.google\.com$/i, 'Google Play URL must be on play.google.com'));
/* Custom slug rules — kept byte-for-byte in step with `App\Util\CustomSlug`.
   The server re-validates on write; this only spares the user a round trip. */
const CUSTOM_SLUG_MIN = 3;
const CUSTOM_SLUG_MAX = 64;
const CUSTOM_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])?$/;
/** The canonical `ep-`/`ad-`/`tl-` auto-link shape. A slug matching it would
 *  be ambiguous with a generated URL, so it's refused on both sides. */
const CUSTOM_SLUG_RESERVED_PATTERN = /^[a-z]{2}-[a-zA-Z0-9]{8,12}$/;
export const customSlugSchema = string()
    .optional()
    .transform((value) => (typeof value === 'string' ? value.trim().toLowerCase() : value))
    .test('custom-slug-length', `Custom URL must be between ${CUSTOM_SLUG_MIN} and ${CUSTOM_SLUG_MAX} characters.`, (value) => !value || (value.length >= CUSTOM_SLUG_MIN && value.length <= CUSTOM_SLUG_MAX))
    .test('custom-slug-consecutive-hyphens', 'Custom URL may not contain consecutive hyphens.', (value) => !value || !value.includes('--'))
    .test('custom-slug-charset', 'Custom URL may only contain lowercase letters, digits and hyphens, and must start and end with a letter or digit.', (value) => !value || CUSTOM_SLUG_PATTERN.test(value))
    .test('custom-slug-reserved', 'That format is reserved for auto-generated links. Pick something more descriptive.', (value) => !value || !CUSTOM_SLUG_RESERVED_PATTERN.test(value));
const isAutoStore = (value) => value.mode === 'auto_store' || (!value.mode && Boolean(value.storeAutoDetectEnabled));
const autoStoreAtLeastOneTest = {
    name: 'auto-store-at-least-one',
    message: 'Provide an App Store URL, Google Play URL, or fallback URL.',
    test: (value) => {
        if (!isAutoStore(value)) {
            return true;
        }
        return Boolean((value.appStoreUrl && value.appStoreUrl.trim() !== '') ||
            (value.googlePlayUrl && value.googlePlayUrl.trim() !== '') ||
            (value.fallbackUrl && value.fallbackUrl.trim() !== ''));
    },
};
const feedbackTitleTest = {
    name: 'feedback-title-required',
    message: 'Add a feedback form title.',
    test: (value) => {
        if (value.mode !== 'feedback')
            return true;
        return Boolean(value.feedbackTitle && value.feedbackTitle.trim() !== '');
    },
};
const feedbackQuestionSchema = object({
    id: string().required(),
    label: string().trim().max(120, 'Question must be 120 characters or fewer.').required('Add a question.'),
    type: mixed().oneOf(['short_text', 'paragraph', 'rating', 'choice']).required(),
    required: boolean().optional(),
    options: array()
        .of(string().required())
        .optional()
        // A choice question needs at least one option; other types ignore it.
        .when('type', {
        is: 'choice',
        then: (schema) => schema.min(1, 'Add at least one option.'),
    }),
    multiple: boolean().optional(),
});
const showroomFields = {
    showroomTagline: string().optional(),
    showroomCurrency: string().optional(),
    showroomCallNumber: string().optional(),
    showroomWhatsappNumber: string().optional(),
    showroomTelegramHandle: string().optional(),
    showroomDirectionsUrl: optionalUrl(),
    createdFromTemplate: string().optional(),
    pageDesign: string().optional(),
    backgroundPattern: string().optional(),
    randomBackgroundPattern: boolean().optional(),
};
export const campaignWithTrackingLinksSchema = object({
    name: string().required(),
    description: string().max(4000, 'Description must be 4000 characters or fewer.').optional(),
    customSlug: customSlugSchema,
    mode: modeSchema,
    trackingLinks: array().of(trackingLinkSchema).required(),
    storeAutoDetectEnabled: boolean().optional(),
    fallbackUrl: optionalUrl(),
    appStoreUrl: appStoreUrlSchema,
    googlePlayUrl: googlePlayUrlSchema,
    feedbackTitle: string().optional(),
    feedbackThankYouMessage: string().optional(),
    feedbackTelegramChatId: string().optional(),
    feedbackCollectName: boolean().optional(),
    feedbackCollectEmail: boolean().optional(),
    feedbackQuestions: array().of(feedbackQuestionSchema).optional(),
    ...showroomFields,
})
    .test(autoStoreAtLeastOneTest)
    .test(feedbackTitleTest);
export const campaignSchema = object({
    name: string().required(),
    description: string().max(4000, 'Description must be 4000 characters or fewer.').optional(),
    customSlug: customSlugSchema,
    mode: modeSchema,
    storeAutoDetectEnabled: boolean().optional(),
    fallbackUrl: optionalUrl(),
    appStoreUrl: appStoreUrlSchema,
    googlePlayUrl: googlePlayUrlSchema,
    feedbackTitle: string().optional(),
    feedbackThankYouMessage: string().optional(),
    feedbackTelegramChatId: string().optional(),
    feedbackCollectName: boolean().optional(),
    feedbackCollectEmail: boolean().optional(),
    feedbackQuestions: array().of(feedbackQuestionSchema).optional(),
    trackingLinks: array().of(trackingLinkSchema).optional(),
    ...showroomFields,
})
    .test(autoStoreAtLeastOneTest)
    .test(feedbackTitleTest);
